import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFeedbacks } from '../../api';
import '../../App.css';

const Feedbacks = () => {
    const navigate = useNavigate();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filters & Pagination state
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'email_asc', 'topic_asc'
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const fetchFeedbacks = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await getFeedbacks();
            // Ensure res is array
            const dataList = Array.isArray(res) ? res : (res?.data || []);
            setFeedbacks(dataList);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error('Error fetching feedbacks:', err);
            setError(err.response?.data?.message || 'Failed to fetch user feedbacks. Please check your connection.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        fetchFeedbacks();
    }, [navigate, fetchFeedbacks]);

    // Handle Copy to Clipboard
    const copyToClipboard = (text, labelId) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedId(labelId);
        setTimeout(() => {
            setCopiedId(null);
        }, 2000);
    };

    // Calculate Summary Statistics
    const metrics = useMemo(() => {
        const total = feedbacks.length;
        const uniqueUsers = new Set(feedbacks.map(f => f.contact_email || f.user_id).filter(Boolean)).size;

        const now = new Date();
        const past24hCutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const past7dCutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const recent24h = feedbacks.filter(f => f.created_at && new Date(f.created_at) >= past24hCutoff).length;
        const recent7d = feedbacks.filter(f => f.created_at && new Date(f.created_at) >= past7dCutoff).length;

        return { total, uniqueUsers, recent24h, recent7d };
    }, [feedbacks]);

    // Filter and Sort Feedbacks
    const filteredFeedbacks = useMemo(() => {
        let result = [...feedbacks];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(item =>
                (item.topic && item.topic.toLowerCase().includes(term)) ||
                (item.problem_description && item.problem_description.toLowerCase().includes(term)) ||
                (item.contact_email && item.contact_email.toLowerCase().includes(term)) ||
                (item.user_id && item.user_id.toLowerCase().includes(term)) ||
                (item.id && item.id.toLowerCase().includes(term))
            );
        }

        result.sort((a, b) => {
            if (sortOrder === 'newest') {
                return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            }
            if (sortOrder === 'oldest') {
                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
            }
            if (sortOrder === 'email_asc') {
                return (a.contact_email || '').localeCompare(b.contact_email || '');
            }
            if (sortOrder === 'topic_asc') {
                return (a.topic || '').localeCompare(b.topic || '');
            }
            return 0;
        });

        return result;
    }, [feedbacks, searchTerm, sortOrder]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredFeedbacks.length / pageSize) || 1;
    const paginatedFeedbacks = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredFeedbacks.slice(start, start + pageSize);
    }, [filteredFeedbacks, currentPage, pageSize]);

    // Reset pagination when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, pageSize, sortOrder]);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const formatRelativeTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr);
            const now = new Date();
            const diffMs = now - d;
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 30) return `${diffDays}d ago`;
            return '';
        } catch {
            return '';
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '36px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header & Title */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '24px',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '20px'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '2.2rem' }}>📬</span>
                            <h1 style={{
                                color: '#3c5a5d',
                                margin: 0,
                                fontSize: '2.2rem',
                                fontWeight: '800'
                            }}>
                                User Feedbacks
                            </h1>
                        </div>
                        <p style={{ margin: '6px 0 0 0', color: '#707F81', fontSize: '0.98rem', fontWeight: '500' }}>
                            View and respond to feedback, bug reports, and suggestions submitted by users
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {lastUpdated && (
                            <span style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                Updated: {lastUpdated}
                            </span>
                        )}
                        <button
                            onClick={fetchFeedbacks}
                            disabled={loading}
                            style={{
                                background: '#3c5a5d',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 18px',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                opacity: loading ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#2c4345')}
                            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#3c5a5d')}
                        >
                            <span style={{
                                display: 'inline-block',
                                transform: loading ? 'rotate(360deg)' : 'none',
                                transition: 'transform 1s linear'
                            }}>
                                🔄
                            </span>
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {/* Error Banner */}
                {error && (
                    <div style={{
                        background: '#fef2f2',
                        borderLeft: '4px solid #ef4444',
                        color: '#991b1b',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <strong>Error:</strong> {error}
                        </div>
                        <button
                            onClick={fetchFeedbacks}
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '6px 14px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Summary Metrics Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        padding: '20px 24px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                        }}>
                            📊
                        </div>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#707F81', fontWeight: '600', textTransform: 'uppercase' }}>
                                Total Feedbacks
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b' }}>
                                {loading ? '...' : metrics.total}
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: '#ffffff',
                        padding: '20px 24px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#ecfdf5',
                            color: '#059669',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                        }}>
                            👤
                        </div>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#707F81', fontWeight: '600', textTransform: 'uppercase' }}>
                                Unique Senders
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b' }}>
                                {loading ? '...' : metrics.uniqueUsers}
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: '#ffffff',
                        padding: '20px 24px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#f5f3ff',
                            color: '#7c3aed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                        }}>
                            ⚡
                        </div>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#707F81', fontWeight: '600', textTransform: 'uppercase' }}>
                                Last 24 Hours
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b' }}>
                                {loading ? '...' : metrics.recent24h}
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: '#ffffff',
                        padding: '20px 24px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#fffbeb',
                            color: '#d97706',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                        }}>
                            📅
                        </div>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#707F81', fontWeight: '600', textTransform: 'uppercase' }}>
                                Last 7 Days
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b' }}>
                                {loading ? '...' : metrics.recent7d}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Controls Bar */}
                <div style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    marginBottom: '24px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Search Bar */}
                    <div style={{ flex: '1 1 300px', position: 'relative' }}>
                        <span style={{
                            position: 'absolute',
                            left: '14px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#94a3b8',
                            fontSize: '1rem'
                        }}>
                            🔍
                        </span>
                        <input
                            type="text"
                            placeholder="Search by topic, email, user ID, or content..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 14px 10px 42px',
                                borderRadius: '10px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3c5a5d'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                }}
                            >
                                ✖
                            </button>
                        )}
                    </div>

                    {/* Sorting & Page Size */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Sort:</span>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                style={{
                                    padding: '9px 14px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    background: '#ffffff',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    color: '#334155',
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="email_asc">Email (A-Z)</option>
                                <option value="topic_asc">Topic (A-Z)</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Per page:</span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                style={{
                                    padding: '9px 12px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    background: '#ffffff',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    color: '#334155',
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    overflow: 'hidden'
                }}>
                    {loading ? (
                        <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🔄</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>Loading user feedbacks...</div>
                        </div>
                    ) : filteredFeedbacks.length === 0 ? (
                        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📭</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#334155', fontSize: '1.25rem' }}>
                                {searchTerm ? 'No matching feedbacks found' : 'No user feedbacks yet'}
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>
                                {searchTerm ? 'Try adjusting your search criteria.' : 'When users submit feedback or contact forms, they will appear here.'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '16px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Date
                                        </th>
                                        <th style={{ padding: '16px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Topic
                                        </th>
                                        <th style={{ padding: '16px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Contact Email & User ID
                                        </th>
                                        <th style={{ padding: '16px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Problem Description
                                        </th>
                                        <th style={{ padding: '16px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedFeedbacks.map((item, index) => {
                                        const relativeTime = formatRelativeTime(item.created_at);
                                        return (
                                            <tr
                                                key={item.id || index}
                                                style={{
                                                    borderBottom: '1px solid #f1f5f9',
                                                    transition: 'background 0.15s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                            >
                                                {/* Date */}
                                                <td style={{ padding: '16px 20px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {formatDate(item.created_at)}
                                                    </div>
                                                    {relativeTime && (
                                                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                                                            {relativeTime}
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Topic */}
                                                <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        background: '#e0f2fe',
                                                        color: '#0369a1',
                                                        padding: '4px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '700',
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item.topic || 'General Feedback'}
                                                    </span>
                                                </td>

                                                {/* Contact Email & User ID */}
                                                <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                                                    {item.contact_email ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                            <a
                                                                href={`mailto:${item.contact_email}?subject=Re: ${encodeURIComponent(item.topic || 'Your feedback')}`}
                                                                style={{
                                                                    fontWeight: '600',
                                                                    color: '#2563eb',
                                                                    textDecoration: 'none',
                                                                    fontSize: '0.9rem'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                            >
                                                                {item.contact_email}
                                                            </a>
                                                            <button
                                                                onClick={() => copyToClipboard(item.contact_email, `email-${item.id}`)}
                                                                title="Copy email"
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.8rem',
                                                                    padding: '2px 4px',
                                                                    color: copiedId === `email-${item.id}` ? '#16a34a' : '#94a3b8'
                                                                }}
                                                            >
                                                                {copiedId === `email-${item.id}` ? '✓' : '📋'}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No email provided</div>
                                                    )}

                                                    {item.user_id && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span style={{
                                                                fontFamily: 'monospace',
                                                                fontSize: '0.75rem',
                                                                background: '#f1f5f9',
                                                                color: '#475569',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px'
                                                            }}>
                                                                User: {item.user_id.substring(0, 8)}...
                                                            </span>
                                                            <button
                                                                onClick={() => copyToClipboard(item.user_id, `user-${item.id}`)}
                                                                title="Copy User ID"
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75rem',
                                                                    color: copiedId === `user-${item.id}` ? '#16a34a' : '#94a3b8'
                                                                }}
                                                            >
                                                                {copiedId === `user-${item.id}` ? '✓' : '📋'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Problem Description */}
                                                <td style={{ padding: '16px 20px', verticalAlign: 'top', maxWidth: '380px' }}>
                                                    <div style={{
                                                        fontSize: '0.9rem',
                                                        color: '#334155',
                                                        lineHeight: '1.5',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {item.problem_description || item.description || 'No description provided.'}
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td style={{ padding: '16px 20px', verticalAlign: 'top', textAlign: 'right' }}>
                                                    <button
                                                        onClick={() => setSelectedFeedback(item)}
                                                        style={{
                                                            background: '#3c5a5d',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            padding: '7px 14px',
                                                            borderRadius: '8px',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'background 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = '#2c4345'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = '#3c5a5d'}
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination Footer */}
                    {!loading && filteredFeedbacks.length > 0 && (
                        <div style={{
                            padding: '16px 24px',
                            background: '#f8fafc',
                            borderTop: '1px solid #e2e8f0',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <div style={{ fontSize: '0.88rem', color: '#64748b' }}>
                                Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, filteredFeedbacks.length)}</strong> of <strong>{filteredFeedbacks.length}</strong> feedbacks
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        background: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                                        color: currentPage === 1 ? '#94a3b8' : '#334155',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    ◀ Prev
                                </button>

                                <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#334155', padding: '0 4px' }}>
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        background: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
                                        color: currentPage === totalPages ? '#94a3b8' : '#334155',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Next ▶
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Detail View */}
            {selectedFeedback && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.6)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '20px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                        padding: '32px',
                        position: 'relative'
                    }}>
                        {/* Close button */}
                        <button
                            onClick={() => setSelectedFeedback(null)}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                background: '#f1f5f9',
                                border: 'none',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                fontSize: '1.1rem',
                                color: '#64748b',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e2e8f0';
                                e.currentTarget.style.color = '#1e293b';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.color = '#64748b';
                            }}
                        >
                            ✖
                        </button>

                        {/* Modal Header */}
                        <div style={{ marginBottom: '24px', paddingRight: '40px' }}>
                            <span style={{
                                display: 'inline-block',
                                background: '#e0f2fe',
                                color: '#0369a1',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                fontWeight: '700',
                                marginBottom: '10px'
                            }}>
                                {selectedFeedback.topic || 'General Feedback'}
                            </span>
                            <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.6rem', fontWeight: '800' }}>
                                Feedback Details
                            </h2>
                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                                Submitted on {formatDate(selectedFeedback.created_at)}
                            </div>
                        </div>

                        {/* Modal Body Info Fields */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                            {/* Email Card */}
                            <div style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Contact Email
                                </div>
                                {selectedFeedback.contact_email ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <a
                                            href={`mailto:${selectedFeedback.contact_email}?subject=Re: ${encodeURIComponent(selectedFeedback.topic || 'Your Feedback')}`}
                                            style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none', fontSize: '0.95rem' }}
                                        >
                                            {selectedFeedback.contact_email}
                                        </a>
                                        <button
                                            onClick={() => copyToClipboard(selectedFeedback.contact_email, 'modal-email')}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === 'modal-email' ? '#16a34a' : '#94a3b8' }}
                                        >
                                            {copiedId === 'modal-email' ? '✓ Copied' : '📋 Copy'}
                                        </button>
                                    </div>
                                ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>N/A</span>
                                )}
                            </div>

                            {/* User ID Card */}
                            <div style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                                    User ID
                                </div>
                                {selectedFeedback.user_id ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155' }}>
                                            {selectedFeedback.user_id}
                                        </span>
                                        <button
                                            onClick={() => copyToClipboard(selectedFeedback.user_id, 'modal-user')}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === 'modal-user' ? '#16a34a' : '#94a3b8', fontSize: '0.8rem' }}
                                        >
                                            {copiedId === 'modal-user' ? '✓ Copied' : '📋 Copy'}
                                        </button>
                                    </div>
                                ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>N/A</span>
                                )}
                            </div>
                        </div>

                        {/* Description Box */}
                        <div style={{ marginBottom: '24px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
                                Full Problem Description / Message
                            </div>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '18px',
                                fontSize: '0.98rem',
                                color: '#1e293b',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                minHeight: '100px'
                            }}>
                                {selectedFeedback.problem_description || selectedFeedback.description || 'No description provided.'}
                            </div>
                        </div>

                        {/* Metadata Box */}
                        <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '14px 16px', marginBottom: '28px', fontSize: '0.82rem', color: '#64748b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>Feedback ID:</span>
                                <span style={{ fontFamily: 'monospace', color: '#334155' }}>{selectedFeedback.id}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>Created At:</span>
                                <span style={{ color: '#334155' }}>{selectedFeedback.created_at}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Updated At:</span>
                                <span style={{ color: '#334155' }}>{selectedFeedback.updated_at}</span>
                            </div>
                        </div>

                        {/* Modal Action Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            {selectedFeedback.contact_email && (
                                <a
                                    href={`mailto:${selectedFeedback.contact_email}?subject=Re: ${encodeURIComponent(selectedFeedback.topic || 'Your Feedback')}`}
                                    style={{
                                        background: '#2563eb',
                                        color: '#ffffff',
                                        textDecoration: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '10px',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    ✉ Reply via Email
                                </a>
                            )}
                            <button
                                onClick={() => setSelectedFeedback(null)}
                                style={{
                                    background: '#cbd5e1',
                                    color: '#334155',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '10px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Feedbacks;
